import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  type SelectChangeEvent
} from '@mui/material'
import { useSessionContext } from 'session'
import {
  type Committee, type CommitteeBasicInformation, getComBasicInfo,
  getCommitteeMembers,
  type LegRole,
  type MemberInformation,
  patchCommitteeMembersDisplayOrder,
  type Role
} from 'services'
import { useParams } from 'react-router-dom'
import { type HttpResponse } from 'interfaces'
import { US_COMMITTEE_MEMBER_LEG_ROLES, US_COMMITTEE_MEMBER_ROLES, US_COMMITTEE_ROLES, MajMinAffilication, STATE_CHAMBERS, CHAMBERS } from 'utils'
import { MemberAutocomplete } from 'components/us/member/member-autocomplete'
import { AlertDialog, CommitteeMemberGroupCard, CommitteeMembersHeaderCard, useGlobalMessageHandler, LegRoleSelect } from 'components'
import { addCommitteeMember, retireCommitteeMember } from 'services'

export interface CommitteeMember {
  id: number
  committee: Committee
  member: MemberInformation
  legRole: LegRole
  role: Role
  displayOrder: number
  updatedBy: string
  lastUpdated: string
}

export const CommitteeMembersPage: React.FC = () => {
  const { comId } = useParams<{ comId: string }>()
  const parsedComId = comId != null ? parseInt(comId, 10) : 0
  const [committee, setCommittee] = useState<CommitteeBasicInformation | undefined>()
  const initialLegRole = useMemo(() => {
    switch (committee?.role.id) {
      case US_COMMITTEE_ROLES.FED_CAUCUS:
        return US_COMMITTEE_MEMBER_LEG_ROLES.FED_CAUCUS_MEMBER
      case US_COMMITTEE_ROLES.STATE_CAUCUS:
        return US_COMMITTEE_MEMBER_LEG_ROLES.STATE_CAUCUS_MEMBER
      case US_COMMITTEE_ROLES.STATE_COMMITTEE:
      case US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE:
        return US_COMMITTEE_MEMBER_LEG_ROLES.STATE_COMMITTEE_MEMBER
      case US_COMMITTEE_ROLES.FED_COMMITTEE:
      case US_COMMITTEE_ROLES.FED_SUBCOMMITTEE:
      default:
        return US_COMMITTEE_MEMBER_LEG_ROLES.FED_COMMITTEE_MEMBER
    }
  }, [committee?.role.id])
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const [membersMajority, setMembersMajority] = useState<CommitteeMember[]>([])
  const [membersMinority, setMembersMinority] = useState<CommitteeMember[]>([])
  const [membersOther, setMembersOther] = useState<CommitteeMember[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [isCreatingPerson, setIsCreatingPerson] = useState(false)
  const [addingToType, setAddingToType] = useState<keyof typeof MajMinAffilication | ''>('')
  const [showRetireConfirmation, setShowRetireConfirmation] = useState(false)
  const [memberToRetire, setMemberToRetire] = useState<CommitteeMember | null>(null)
  const [selectedPerson, setSelectedPerson] = useState<MemberInformation | undefined>(undefined)
  const [selectedLegRoleId, setSelectedLegRoleId] = useState<number>(initialLegRole)
  const { showSuccessMessage, showErrorMessage, clearMessages } = useGlobalMessageHandler()

  useEffect(() => {
    setSelectedLegRoleId(initialLegRole)
  }, [initialLegRole])

  if (parsedComId === 0) {
    return (
      <Box>Invalid Committee ID</Box>
    )
  }

  useEffect(() => {
    changeModuleName('Committee Members')
    void loadMembers()
  }, [comId])

  // Add this new useEffect to set the document title and uniqueId when committee data is loaded
  useEffect(() => {
    if (committee?.id != null) {
      changeUniqueId(`${parsedComId} - ${committee.name}`)
      document.title = `Members of Com ${parsedComId} - ${committee.name}`
    } else {
      changeUniqueId('')
    }
  }, [committee, parsedComId])

  const loadMembers = async (): Promise<void> => {
    if (comId != null) {
      setLoading(true)
      try {
        const value = await getCommitteeMembers(comId)
        const committee = await getComBasicInfo(parsedComId)
        setCommittee(committee)
        setMembersMajority(value.MAJ ?? [])
        setMembersMinority(value.MIN ?? [])
        setMembersOther(value.OTH ?? [])
      } finally {
        setLoading(false)
      }
    }
  }

  const handleDisplayOrderUpdate = async (
    items: CommitteeMember[],
    itemSelected: CommitteeMember
  ): Promise<HttpResponse<CommitteeMember[]>> => {
    const response = await patchCommitteeMembersDisplayOrder(parsedComId, items, itemSelected)

    if (!response.succeeded) {
      return response
    }

    const memberType = itemSelected.member.majmin as keyof typeof MajMinAffilication
    const updatedItems = items.map((item, index) => ({
      ...item,
      displayOrder: index + 1
    }))

    const listMap = {
      MAJ: setMembersMajority,
      MIN: setMembersMinority,
      OTH: setMembersOther
    }

    listMap[memberType](updatedItems)

    return response
  }

  const updateMembersList = (
    memberType: keyof typeof MajMinAffilication,
    operation: 'add' | 'remove',
    member: CommitteeMember
  ): void => {
    const listMap = {
      MAJ: [membersMajority, setMembersMajority],
      MIN: [membersMinority, setMembersMinority],
      OTH: [membersOther, setMembersOther]
    } as const

    const [currentList, setList] = listMap[memberType]

    if (operation === 'add') {
      const maxDisplayOrder = Math.max(...currentList.map(m => m.displayOrder ?? 0), 0)
      const memberWithDisplayOrder = {
        ...member,
        displayOrder: maxDisplayOrder + 1
      }
      setList([...currentList, memberWithDisplayOrder].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)))
    } else {
      setList(currentList.filter(m => m.id !== member.id))
    }
  }

  const handleAddMember = (memberType: keyof typeof MajMinAffilication): void => {
    setAddingToType(memberType)
    setOpenDialog(true)
  }

  const handleMemberSelected = (member: MemberInformation): void => {
    if (addingToType !== '' && member.majmin !== addingToType) {
      const memberTypeLabel = MajMinAffilication[member.majmin as keyof typeof MajMinAffilication]
      const targetTypeLabel = MajMinAffilication[addingToType]
      showErrorMessage(`Member is from ${memberTypeLabel}, cannot add to ${targetTypeLabel}`)
      return
    }
    setSelectedPerson(member)
  }

  const handleLegRoleChange = (event: SelectChangeEvent<number>): void => {
    setSelectedLegRoleId(Number(event.target.value))
  }

  const handleAddMemberWithRole = async (): Promise<void> => {
    try {
      if (selectedPerson == null || selectedLegRoleId == null) {
        showErrorMessage('Please select both a person and a role')
        return
      }

      clearMessages()
      setSaving(true)

      const response = await addCommitteeMember(parsedComId, selectedPerson, selectedLegRoleId)

      if (response.succeeded && (response.result != null) && addingToType !== '') {
        updateMembersList(addingToType, 'add', response.result)
        showSuccessMessage('Member added successfully')
        setOpenDialog(false)
        setSelectedPerson(undefined)
        setSelectedLegRoleId(initialLegRole)
      } else if (!response.succeeded) {
        const errorMsg = response.errors?.member ?? 'Error adding member'
        showErrorMessage(errorMsg)
        setSelectedPerson(undefined)
        setSelectedLegRoleId(initialLegRole)
      }
    } catch (error: any) {
      showErrorMessage('Error adding member')
    } finally {
      setSaving(false)
    }
  }

  const handleRetireMember = (member: CommitteeMember): void => {
    setMemberToRetire(member)
    setShowRetireConfirmation(true)
  }

  const confirmRetireMember = async (): Promise<void> => {
    if (memberToRetire == null) return

    setShowRetireConfirmation(false)
    try {
      setSaving(true)
      const response = await retireCommitteeMember(parsedComId, memberToRetire.id)

      if (response.succeeded) {
        updateMembersList(memberToRetire.member.majmin as keyof typeof MajMinAffilication, 'remove', memberToRetire)
        showSuccessMessage('Member retired successfully')
      }
    } catch (error) {
      showErrorMessage('Error retiring member')
    } finally {
      setSaving(false)
      setMemberToRetire(null)
    }
  }

  const getAvailableRoles = (committeeRoleId: number | undefined): number[] => {
    switch (committeeRoleId) {
      case US_COMMITTEE_ROLES.FED_CAUCUS:
        return [US_COMMITTEE_MEMBER_ROLES.FED_CAUCUS_MEMBER, US_COMMITTEE_MEMBER_ROLES.FED_CAUCUS_OFFICER]
      case US_COMMITTEE_ROLES.STATE_CAUCUS:
        return [US_COMMITTEE_MEMBER_ROLES.STATE_CAUCUS_MEMBER, US_COMMITTEE_MEMBER_ROLES.STATE_CAUCUS_OFFICER]
      case US_COMMITTEE_ROLES.STATE_COMMITTEE:
      case US_COMMITTEE_ROLES.STATE_SUBCOMMITTEE:
        return [US_COMMITTEE_MEMBER_ROLES.STATE_COMMITTEE_MEMBER, US_COMMITTEE_MEMBER_ROLES.STATE_COMMITTEE_OFFICER]
      case US_COMMITTEE_ROLES.FED_COMMITTEE:
      case US_COMMITTEE_ROLES.FED_SUBCOMMITTEE:
        return [US_COMMITTEE_MEMBER_ROLES.FED_COMMITTEE_MEMBER, US_COMMITTEE_MEMBER_ROLES.FED_COMMITTEE_OFFICER]
      default:
        return []
    }
  }

  if (loading) {
    return (
      <Box display={'flex'} height={'90vh'} flex={1} justifyContent={'center'} alignItems={'center'}>
        <CircularProgress size={100}/>
      </Box>
    )
  }

  return (
    <Container maxWidth={false} sx={{ py: 2 }}>
      <Grid container spacing={2}>
        {committee != null && (
          <Grid item xs={12}>
            <CommitteeMembersHeaderCard
              committee={committee}
              majorityCount={membersMajority.length}
              minorityCount={membersMinority.length}
              otherCount={membersOther.length}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <CommitteeMemberGroupCard
            title="Majority"
            members={membersMajority}
            stackId={`committee-majority-${parsedComId}`}
            loading={loading || saving}
            onAdd={() => { handleAddMember('MAJ') }}
            onRetire={handleRetireMember}
            updateDisplayOrder={handleDisplayOrderUpdate}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <CommitteeMemberGroupCard
            title="Minority"
            members={membersMinority}
            stackId={`committee-minority-${parsedComId}`}
            loading={loading || saving}
            onAdd={() => { handleAddMember('MIN') }}
            onRetire={handleRetireMember}
            updateDisplayOrder={handleDisplayOrderUpdate}
          />
        </Grid>
        <Grid item xs={12}>
          <CommitteeMemberGroupCard
            title="Other"
            members={membersOther}
            stackId={`committee-other-${parsedComId}`}
            loading={loading || saving}
            onAdd={() => { handleAddMember('OTH') }}
            onRetire={handleRetireMember}
            updateDisplayOrder={handleDisplayOrderUpdate}
          />
        </Grid>
      </Grid>
      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false)
          setSelectedPerson(undefined)
          setSelectedLegRoleId(initialLegRole)
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            minHeight: '60vh'
          }
        }}
      >
        <DialogTitle>Select Person</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 2 }}>
            <Grid item xs={12}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item xs={12}>
                  <MemberAutocomplete
                    member={selectedPerson}
                    updateMember={handleMemberSelected}
                    filledPerson={false}
                    editable={true}
                    editing={selectedPerson == null}
                    setEditing={() => {}}
                    createPerson={isCreatingPerson}
                    setCreatePerson={setIsCreatingPerson}
                    chamberIds={
                      committee?.chamber?.id === CHAMBERS.US_JOINT.id
                        ? [CHAMBERS.US_HOUSE.id, CHAMBERS.US_SENATE.id]
                        : committee?.chamber?.id === CHAMBERS.STATE_JOINT.id
                          ? [CHAMBERS.STATE_HOUSE.id, CHAMBERS.STATE_SENATE.id]
                          : committee?.chamber?.id !== undefined ? [committee.chamber.id] : undefined
                    }
                    majmin={addingToType !== '' ? addingToType : undefined}
                    stateId={STATE_CHAMBERS.some(c => c.id === committee?.chamber?.id) ? committee?.state?.id : undefined}
                  />
                </Grid>
              </Grid>
            </Grid>
            {selectedPerson != null && (
              <Grid item xs={12} sx={{ mt: 2 }}>
                <FormControl fullWidth>
                  <LegRoleSelect
                    value={selectedLegRoleId}
                    onChange={handleLegRoleChange}
                    disabled={false}
                    label="Role"
                    roleId={getAvailableRoles(committee?.role.id)}
                  />
                </FormControl>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setOpenDialog(false)
            setSelectedPerson(undefined)
            setSelectedLegRoleId(initialLegRole)
          }}>Cancel</Button>
          <Button
            onClick={() => { void handleAddMemberWithRole() }}
            disabled={saving || selectedPerson == null || selectedLegRoleId == null}
            variant="contained"
          >
            Add Member
          </Button>
        </DialogActions>
      </Dialog>
      <AlertDialog
        onClose={() => { setShowRetireConfirmation(false) }}
        open={showRetireConfirmation}
        onButtonClick={() => { void confirmRetireMember() }}
        title="Retire Committee Member"
        contentText="Are you sure you want to retire this committee member?"
        nameButtonCancel='Close'
        nameButtonConfirm='Retire'
        containedButton = {true}
      />
    </Container>
  )
}
